import { EmailIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, Grid, HStack, Img, Link, Stack, Text } from '@chakra-ui/react'
import SectionStack from 'components/common/SectionStack'
import ExternalLink from 'components/links/ExternalLink'
import { AmexLogo } from 'components/svg/AmexLogo'
import { IuguLogo } from 'components/svg/IuguLogo'
import { MastercardLogo } from 'components/svg/MastercardLogo'
import MedleveLogo from 'components/svg/MedleveLogo'
import { SslSecureLogo } from 'components/svg/SslSecureLogo'
import { VisaLogo } from 'components/svg/VisaLogo'
import WhatsApp from 'components/svg/WhatsApp'
import { Body2, Body3, H2 } from 'components/typography'
import { MEDLEVE_SUPPORT_EMAIL, MEDLEVE_WHATSAPP_LINK } from 'constants/omensSupportContact'
import NextLink from 'next/link'
import { FC, ReactNode } from 'react'

// Reuse only labels/icons and unchanged links; the organic Instagram uses Consolidado §7.
// The shared footer component and its paid landing profile list remain frozen.
import { SOCIAL_LINKS_MEDLEVE } from '../../../../layouts/LadingLayout/components/Footer/MedleveFooter'
import { MEDLEVE_LEGAL_ENTITY, MEDLEVE_SAME_AS } from '../../../medleveSeo/constants'
import { HOME_FOOTER_LEGAL } from '../content'
import { getHomeFooterLinks } from '../footerLinks'

const FooterHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <H2
    fontWeight={{ base: 700, lg: 600 }}
    fontSize={{ base: '1rem', lg: '17px' }}
    lineHeight={{ base: '1.25rem', lg: '24px' }}
    letterSpacing="-0.02em"
    pb="4px"
    borderBottom="2px solid #25241E"
  >
    {children}
  </H2>
)

/**
 * Footer of the organic homepage — a VARIANT of the shared `MedleveFooter` (PRD §32: the paid landings
 * keep the original untouched). Same look (logo, contact, legal text, payment/safety seals); differences:
 * - brand is fixed (no cookie lookup), so it server-renders;
 * - adds the PRD §32 navigation, limited to URLs that exist today (see footerLinks.ts);
 * - the short "Sobre a Medleve" blurb is not repeated here: it promised "prescrição personalizada" and
 *   "entrega do tratamento na sua casa" (PRD §6/§13) and the page now has its own "Sobre a Medleve" section;
 * - states the legal entity explicitly (PRD §5).
 * REQUIRES LEGAL REVIEW — legal texts are verbatim copies of the current footer; do not edit.
 */
const HomeFooter: FC = () => (
  <Box as="footer" bg="darkchoc" color="alabaster">
    <Stack maxW={1280} m="0 auto" pt={4} pb={24}>
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }}>
        <SectionStack>
          <MedleveLogo role="img" aria-label="Medleve" color="white" maxW={{ base: '150px', md: '250px' }} h="auto" />
        </SectionStack>

        <SectionStack>
          <FooterHeading>Contato</FooterHeading>
          <Text textStyle={{ base: 'h5sm', lg: 'h5' }}>
            Quer uma ajuda mais rápida? Fale com a gente pelo WhatsApp.
          </Text>
          <ExternalLink href={MEDLEVE_WHATSAPP_LINK}>
            <Button
              mt="8px"
              bg="medleve"
              _hover={{ bg: 'medleve' }}
              rightIcon={<WhatsApp color="alabaster" />}
              color="alabaster"
              fontWeight="500"
              minH="48px"
            >
              <Box pl="4px">Conversar no WhatsApp</Box>
            </Button>
          </ExternalLink>
          <ExternalLink href={`mailto:${MEDLEVE_SUPPORT_EMAIL}`} color="alabaster">
            <HStack>
              <EmailIcon boxSize={5} />
              <Text color="alabaster">{MEDLEVE_SUPPORT_EMAIL}</Text>
            </HStack>
          </ExternalLink>
          <SectionStack p={0} mt={2}>
            <Text textStyle={{ base: 'body1sm', lg: 'body1' }} pb="4px" borderBottom="2px solid #25241E">
              Siga nas redes
            </Text>
            <Flex as="ul" listStyleType="none" m={0} p={0} wrap="wrap" sx={{ gap: '2rem' }}>
              {SOCIAL_LINKS_MEDLEVE.map(({ label, href, icon }) => (
                <Box as="li" key={label} display="flex" alignItems="center">
                  <ExternalLink href={label === 'Instagram' ? MEDLEVE_SAME_AS[0] : href} aria-label={label}>
                    <Box as={icon} fill="medleve" aria-hidden="true" />
                  </ExternalLink>
                </Box>
              ))}
            </Flex>
          </SectionStack>
        </SectionStack>
      </Grid>

      <SectionStack as="nav" aria-label="Rodapé">
        <FooterHeading>Navegação</FooterHeading>
        <Flex as="ul" listStyleType="none" m={0} p={0} wrap="wrap" columnGap={8} rowGap={3}>
          {getHomeFooterLinks().map(({ label, href }) => (
            <Box as="li" key={href}>
              <Link as={NextLink} href={href} prefetch={false} color="alabaster" textDecoration="underline">
                <Body2 as="span">{label}</Body2>
              </Link>
            </Box>
          ))}
        </Flex>
      </SectionStack>

      {/* REQUIRES LEGAL REVIEW — verbatim legal texts + legal entity (PRD §5). */}
      <SectionStack>
        <FooterHeading>Informações legais</FooterHeading>
        <Body2 color="alabaster">{HOME_FOOTER_LEGAL.dpo}</Body2>
        <Body2 color="alabaster">{HOME_FOOTER_LEGAL.copyright}</Body2>
        <Body2 color="alabaster">
          {MEDLEVE_LEGAL_ENTITY.legalName} CNPJ {MEDLEVE_LEGAL_ENTITY.cnpj}
        </Body2>
        <Body3 color="alabaster">{HOME_FOOTER_LEGAL.responsibility}</Body3>
      </SectionStack>

      {/* Same seals as the shared `SafetySection`, with intrinsic image dimensions (PRD §71). */}
      <SectionStack>
        <FooterHeading>Protegido por</FooterHeading>
        <Flex wrap="wrap" sx={{ gap: '2rem' }} p="4" align="center" justifyContent="space-between">
          <SslSecureLogo h="46px" w="74px" />
          <VisaLogo />
          <Img
            src="/landings/google-safe-browsing_bw.webp"
            alt="site seguro"
            htmlWidth={495}
            htmlHeight={99}
            loading="lazy"
            h="33px"
            w="165px"
            objectFit="cover"
          />
          <MastercardLogo theme="grayscale" />
          <IuguLogo />
          <AmexLogo />
        </Flex>
      </SectionStack>
    </Stack>
  </Box>
)

export default HomeFooter
